import { supabase } from "@/lib/supabase";
import { computeUpsellTotals } from "@/lib/upsell-totals";

// Re-eksporter mutations + types fra use-budget-line-mutations så
// eksisterende imports fortsetter å fungere uendret (VIB-653).
export * from "./use-budget-line-mutations";

/**
 * Re-aggregér `budget_line_assignments.hours` for én bruker fra `time_entries`.
 * Speiler logikken i edge function `import-monday-time-data` slik at admin kan
 * flytte time_entries til riktig budgetlinje fra UI uten å re-importere.
 *
 * Foreldreløse assignments (budget_lines som ikke lenger har time_entries
 * knyttet til seg for denne brukeren) settes til 0 så gamle tall ikke
 * henger igjen.
 */
export async function recomputeUserAssignments(userId: string): Promise<void> {
  const [entriesRes, existingRes] = await Promise.all([
    supabase
      .from("time_entries")
      .select("budget_line_id, budgeted_hours")
      .eq("user_id", userId)
      .not("budget_line_id", "is", null)
      .not("budgeted_hours", "is", null),
    supabase
      .from("budget_line_assignments")
      .select("budget_line_id")
      .eq("user_id", userId),
  ]);

  if (entriesRes.error) throw new Error(entriesRes.error.message);

  const sumByBudgetLine = new Map<string, number>();
  for (const r of entriesRes.data ?? []) {
    if (!r.budget_line_id || r.budgeted_hours == null) continue;
    const prev = sumByBudgetLine.get(r.budget_line_id) ?? 0;
    sumByBudgetLine.set(r.budget_line_id, prev + Number(r.budgeted_hours));
  }

  const newIds = new Set(sumByBudgetLine.keys());
  const orphaned = (existingRes.data ?? [])
    .map((a) => a.budget_line_id)
    .filter((id) => id && !newIds.has(id)) as string[];
  if (orphaned.length > 0) {
    const { error } = await supabase
      .from("budget_line_assignments")
      .update({ hours: 0 })
      .eq("user_id", userId)
      .in("budget_line_id", orphaned);
    if (error) throw new Error(error.message);
  }

  const rows = Array.from(sumByBudgetLine.entries()).map(([budget_line_id, hours]) => ({
    budget_line_id,
    user_id: userId,
    hours,
  }));
  if (rows.length > 0) {
    const { error } = await supabase
      .from("budget_line_assignments")
      .upsert(rows, { onConflict: "budget_line_id,user_id" });
    if (error) throw new Error(error.message);
  }
}

/**
 * Synkroniser cached `upsells.amount_excl_vat` med kundepris (linjer + overhead
 * − rabatt) via `computeUpsellTotals`. Cache brukes av Faktura-fanen og
 * rapporter. T&M-mersalg holder amount_excl_vat = 0 (VIB-994).
 */
export async function recomputeUpsellAmount(upsellId: string): Promise<void> {
  const { data: upsell } = await supabase
    .from("upsells")
    .select("billing_mode, overhead_pct, discount_pct, discount_amount, project_id")
    .eq("id", upsellId)
    .maybeSingle();
  if (upsell?.billing_mode === "tm") {
    await supabase.from("upsells").update({ amount_excl_vat: 0 }).eq("id", upsellId);
    return;
  }

  const { data: lines } = await supabase
    .from("budget_lines")
    .select("amount, estimated_cost, cost_type, unit")
    .eq("upsell_id", upsellId);

  const { data: metadata } = await supabase
    .from("project_offer_metadata")
    .select("overhead_pct")
    .eq("project_id", upsell?.project_id ?? "")
    .maybeSingle();
  const defaultOverheadPct = Number(metadata?.overhead_pct ?? 0);

  const { total } = computeUpsellTotals({
    lines: (lines ?? []).map((l) => ({
      amount: l.amount != null ? Number(l.amount) : null,
      estimated_cost: l.estimated_cost != null ? Number(l.estimated_cost) : null,
      cost_type: l.cost_type,
      unit: l.unit,
    })),
    overhead_pct: upsell?.overhead_pct != null ? Number(upsell.overhead_pct) : null,
    discount_pct: upsell?.discount_pct != null ? Number(upsell.discount_pct) : null,
    discount_amount: upsell?.discount_amount != null ? Number(upsell.discount_amount) : null,
    defaultOverheadPct,
  });

  const kundepris = Math.round(total * 100) / 100;
  await supabase
    .from("upsells")
    .update({ amount_excl_vat: kundepris })
    .eq("id", upsellId);
}

/**
 * Hold projects.total_amount synket med live-beregnet tilbudssum.
 *
 * Siden migrasjon `20260506130000_total_amount_trigger` har vi en
 * Postgres-trigger på budget_lines, upsells og project_offer_metadata
 * som kaller `compute_project_total(uuid)` automatisk ved hver mutasjon.
 * total_amount holder seg alltid synket — uansett om endringen kommer
 * fra hooks, edge-funksjoner, direkte SQL eller cron.
 *
 * Denne JS-funksjonen er beholdt som idempotent buffer-trigger så
 * ekspliсitte hook-kall fortsatt virker (no-op i praksis siden DB
 * allerede har riktig verdi). Vi kaller compute-funksjonen direkte
 * for å unngå at vi i fremtiden glemmer å speile UI-formelen i to filer.
 */
export async function recomputeProjectTotal(projectId: string): Promise<void> {
  const { data, error } = await supabase.rpc("compute_project_total", {
    p_project_id: projectId,
  });
  if (error) {
    console.error("recomputeProjectTotal failed:", error.message);
    return;
  }
  await supabase
    .from("projects")
    .update({ total_amount: data ?? 0 })
    .eq("id", projectId);
}
